import { CalendarClock, Package, ShieldCheck, ShieldOff, EyeOff, History, ClipboardList, AlertTriangle, Timer, Tag, Fingerprint } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/lib/i18n';
import { fmtDate, isStaleCollection, isReducedScope } from '@/lib/utils';
import type { AuditDoc, AuditWarning } from '@/types/models';

/** Avertissements d'ingestion (AuditDoc.warnings) → libellé. */
const WARNING_KEYS: Record<AuditWarning,
  'audit.warning.integrity_mismatch' | 'audit.warning.unsupported_schema_version'
  | 'audit.warning.sha256_mismatch' | 'audit.warning.redaction_declared_required'
  | 'audit.warning.egress_declared_forbidden'> = {
  integrity_mismatch: 'audit.warning.integrity_mismatch',
  unsupported_schema_version: 'audit.warning.unsupported_schema_version',
  sha256_mismatch: 'audit.warning.sha256_mismatch',
  redaction_declared_required: 'audit.warning.redaction_declared_required',
  egress_declared_forbidden: 'audit.warning.egress_declared_forbidden',
};

/**
 * Métadonnées et badges d'en-tête dérivés de run_metadata (contrats 1.0 et
 * 1.1). Les badges 1.1 (périmètre réduit, étiquettes) ne s'affichent que si
 * `effectiveConfig` / `tags` existent : un audit 1.0 rend exactement comme avant.
 */
export function AuditHeaderBadges({ audit, now }: { audit: AuditDoc; now?: Date }) {
  const { t, locale } = useT();
  const stale = isStaleCollection(audit.collectedAt, 30, now);
  const cfg = audit.effectiveConfig ?? null;
  const reduced = isReducedScope(cfg);
  const reducedTitle = reduced
    ? (cfg!.quick === true ? t('audit.badge.reduced_scope.quick') : t('audit.badge.reduced_scope.budget', { n: cfg!.maxMinutes ?? 0 }))
    : undefined;
  // run_metadata.tags (1.1) : {clé: valeur}. Tolérant à un document sans tags.
  const tags = audit.tags && !Array.isArray(audit.tags)
    ? Object.entries(audit.tags).filter(([, v]) => typeof v === 'string')
    : [];
  return (
    <>
      {audit.toolkitVersion && (
        <span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {t('audit.meta.toolkit_version', { v: audit.toolkitVersion })}</span>
      )}
      {audit.collectedAt && (
        <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> {t('audit.meta.collected_at', { when: fmtDate(audit.collectedAt, locale) })}</span>
      )}
      {/* Empreinte de l'archive réellement ingérée (ING-19 / SEC-08) : preuve
          de ce que le portail a traité. Absente des audits antérieurs. */}
      {audit.zipSha256 && (
        <span className="inline-flex items-center gap-1" title={t('audit.meta.zip_sha256.title', { hash: audit.zipSha256 })}>
          <Fingerprint className="h-3.5 w-3.5" />
          <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.7rem] dark:bg-ink-700">
            {t('audit.meta.zip_sha256', { hash: audit.zipSha256.slice(0, 12) })}
          </code>
        </span>
      )}
      {audit.platform && (
        // summary.os est le libellé CORRIGÉ (« Windows 11 (build …) » — release
        // ment sur les éditions client) ; platform.release ne sert que de repli
        // pour les audits ingérés avant le correctif.
        <span>{t('audit.meta.platform', {
          os: audit.summary?.os ?? `${audit.platform.system} ${audit.platform.release}`,
          machine: audit.platform.machine,
        })}</span>
      )}
      {stale && <Badge variant="warning"><History className="mr-1 inline h-3 w-3" />{t('audit.badge.stale')}</Badge>}
      {audit.redacted && <Badge variant="info"><EyeOff className="mr-1 inline h-3 w-3" />{t('audit.badge.redacted')}</Badge>}
      {audit.legacy && <Badge variant="warning">{t('audit.badge.legacy')}</Badge>}
      {audit.interrupted && <Badge variant="danger">{t('audit.badge.interrupted')}</Badge>}
      {audit.partial && !audit.interrupted && <Badge variant="warning">{t('audit.badge.partial')}</Badge>}
      {reduced && <Badge variant="warning" title={reducedTitle}><Timer className="mr-1 inline h-3 w-3" />{t('audit.badge.reduced_scope')}</Badge>}
      {audit.executedAsAdmin === true && <Badge variant="neutral"><ShieldCheck className="mr-1 inline h-3 w-3" />{t('audit.badge.admin')}</Badge>}
      {audit.executedAsAdmin === false && <Badge variant="neutral"><ShieldOff className="mr-1 inline h-3 w-3" />{t('audit.badge.no_admin')}</Badge>}
      {audit.questionnaireStatus === 'missing' && <Badge variant="neutral"><ClipboardList className="mr-1 inline h-3 w-3" />{t('audit.badge.no_questionnaire')}</Badge>}
      {tags.map(([key, value]) => (
        <Badge key={key} variant="info" title={t('audit.scope.tags')}><Tag className="mr-1 inline h-3 w-3" />{key}={value}</Badge>
      ))}
      {(audit.warnings ?? []).filter(w => WARNING_KEYS[w]).map(w => (
        <Badge key={w} variant="warning" title={t(WARNING_KEYS[w])}><AlertTriangle className="mr-1 inline h-3 w-3" />{t(WARNING_KEYS[w])}</Badge>
      ))}
    </>
  );
}
